import { _ } from "../../../polyglot/polyglot.js";
import { trashAltIcon } from "../../../elements/icons/index.js";
import AbstractWidgetsListStrategy from "../../WidgetsListWidget/strategies/AbstractWidgetsListStrategy.js";
import button from "@foretagsplatsen/finsit-design-system/widget-js/elements/button";
import makeTransactionFieldForm from "./makeTransactionFieldForm.js";

/**
 * Strategy to manage a list of TransactionField widgets.
 *
 * It delegates the handling of the state to a ManualJournalEntryRuleModel
 * instance.
 * It also externalizes the rendering of validation errors.
 *
 * @param {Account[]} allAccounts - List of all available accounts that can be
 *   used for creating a manual entry
 * @param {ManualJournalEntryRuleModel} manualJournalEntryRuleModel - Model
 *   handling the list of transactions and their validity
 * @param {function} [extraValidator] - Validator invoked with an
 *   AlertCollector and a context literal object holding the
 *   transactions to validate.
 */
export default class TransactionsStrategy extends AbstractWidgetsListStrategy {
	constructor({
		allAccounts,
		manualJournalEntryRuleModel,
		extraValidator = () => {},
		onInputChange,
	}) {
		super(...arguments);
		this._allAccounts = allAccounts;
		this._manualJournalEntryRuleModel = manualJournalEntryRuleModel;
		this._extraValidator = extraValidator;
		this._onInputChange = onInputChange;

		this._itemToForm = new Map();
	}

	getTransactions() {
		return this._manualJournalEntryRuleModel.getTransactions();
	}

	shouldExternalizeValidationRendering() {
		return true;
	}

	extraCSSClasses() {
		return "TransactionFields";
	}

	canDeleteItem({ item: _item, index: _index }) {
		return this._getItemsOrEmpty().length > 2;
	}

	itemRenderer({ item, index }) {
		const form = makeTransactionFieldForm({
			allAccounts: this._makeAccountItems({
				selectedAccount: item.getAccount(),
			}),
			transaction: item,
			hideLabel: index > 0,
			onInputChange: this._onInputChange,
		});

		this._itemToForm.set(item, form);

		return (html) =>
			html.div(
				{ class: "TransactionField", "data-test": "transaction-field" },
				form,
			);
	}

	addButtonRenderer({ onClick }) {
		return (html) =>
			html.span(
				{
					class: "icon-and-text link-text",
					"data-test": "add-row",
					click: onClick,
				},
				html.i({ class: "far fa-circle-plus" }),
				_("Add row"),
			);
	}

	deleteButtonRenderer({ onClick }) {
		return button(
			{ as: "span", onClick },
			trashAltIcon({
				class: "delete-icon fa-large",
				dataTest: "delete",
			}),
		);
	}

	addNewItem() {
		this._manualJournalEntryRuleModel.addBalancedTransaction(
			this._makeNewItem(),
		);
	}

	deleteItem({ item }) {
		this._manualJournalEntryRuleModel.removeTransaction(item);
	}

	_makeAccountItems({ selectedAccount }) {
		return this._allAccounts.map((account) => ({
			label: account.toString(),
			value: account,
			selected: account === selectedAccount,
			match: (searchString) => account.match(searchString),
		}));
	}

	_makeNewItem() {
		return {
			account: undefined,
		};
	}

	_getItemsOrEmpty() {
		return this._manualJournalEntryRuleModel.getTransactions();
	}

	_validate({ collector }) {
		this._manualJournalEntryRuleModel.validate({ collector });

		this._extraValidator({
			collector,
			context: {
				transactions: this._getItemsOrEmpty(),
			},
		});
	}

	_validateItem({ item, collector }) {
		const isValid = this._itemToForm.get(item)?.validate();
		collector.addErrorIf(!isValid, _("Invalid field"));
	}
}
